import React, { useEffect, useState, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { validatePayment, markOrderAsTracked } from '../../services/api/payments';
import { Order, OrderStatus } from '../../entities/order';
import { strings, parameters } from '../../localization/strings';
import { COOKIE_NAME_ANSWER } from '../../context/questionnaire';
import ContainerMd from '../../components/common/containers/containerMd';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import CommonAnimatedSpinner from '../../components/common/animated/spinner';
import { useSite } from '../../hooks/use-site';
import { ShortAnswerProduct } from '../../entities/short-answer';
import { getLocalizedPath } from '../../helpers/localization-helper';
import { useAuth } from '../../hooks/use-auth';
import { getProductSlug, getProductName } from '../../utils/product-names';
import { pushGTMEvent, GTM_EVENTS } from '../../utils/gtm-events';

const cookies = new Cookies();

export default function PaymentsSuccessPageComponent() {
  const { hash, productId } = useParams();
  const { locale, region } = useSite();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order>();
  const [product, setProduct] = useState<ShortAnswerProduct>();
  const [isWaiting, setIsWaiting] = useState<boolean>(true);
  const [isResearch, setIsResearch] = useState<boolean>(false);
  const [hasExtraSurvey, setHasExtraSurvey] = useState<boolean>(false);

  const analyticsProcessedRef = useRef<boolean>(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const analyticsStorageKey = `analytics_tracked_${hash}`;

  const trackPurchaseAnalytics = useCallback(
    (orderData: Order) => {
      let sessionTracked;

      try {
        sessionTracked = sessionStorage.getItem(analyticsStorageKey);
      } catch (error) {
        sessionTracked = null;
      }

      if (orderData.analyticsTracked || analyticsProcessedRef.current || sessionTracked) {
        return;
      }

      analyticsProcessedRef.current = true;
      sessionStorage.setItem(analyticsStorageKey, 'true');

      try {
        const productSlug = orderData.productId ? getProductSlug(orderData.productId) : undefined;
        const productName = orderData.productId ? getProductName(orderData.productId) : orderData.productTitle;

        pushGTMEvent(GTM_EVENTS.PURCHASE, {
          ecommerce: {
            transaction_id: orderData.id,
            value: orderData.amount,
            currency: locale === 'pl' ? 'PLN' : 'EUR',
            coupon: orderData.couponCode,
            customer_id: orderData.customerId,
            items: [
              {
                item_id: orderData.productId,
                item_name: productName,
                item_variant: productSlug,
                quantity: 1,
                price: orderData.amount,
              },
            ],
          },
        });

        markOrderAsTracked(hash || '').catch(() => {
          // Silently fail - analytics tracking is not critical for user experience
        });
      } catch (error) {
        analyticsProcessedRef.current = false;
        sessionStorage.removeItem(analyticsStorageKey);
      }
    },
    [hash, locale, analyticsStorageKey],
  );

  useEffect(() => {
    if (productId) {
      setIsResearch(parseInt(productId, 10) === parameters().product.researchId);
    }

    const checkForPayment = async () => {
      const res = await validatePayment(hash || '');

      setOrder(res.order);
      setIsWaiting(res.order.status === OrderStatus.New);
      setHasExtraSurvey(!!res.product.extraSurveyId);
      setProduct(res.product);

      return res.order;
    };

    if (!timerRef.current) {
      timerRef.current = setInterval(async () => {
        const orderData = await checkForPayment();

        const isOrderComplete =
          (orderData.status !== OrderStatus.New && !orderData.isGift) ||
          (orderData.status !== OrderStatus.New && orderData.pdfId && orderData.isGift);

        if (isOrderComplete) {
          cookies.remove(COOKIE_NAME_ANSWER, { path: '/' });

          trackPurchaseAnalytics(orderData);

          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }

          // Refresh user data to include the new order
          await refreshUser();

          if (orderData.isGift) {
            navigate(getLocalizedPath('/profile/dashboard', locale, region));
          }
        }
      }, 2000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [hash, navigate, locale, productId, trackPurchaseAnalytics, refreshUser]);

  return (
    <div className="px-4">
      {isWaiting && (
        <div className="min-h-screen text-center pt-10 flex flex-col items-center justify-center relative text-lg">
          <div className="mb-3">
            <CommonAnimatedSpinner />
          </div>

          {!isResearch && <span>{strings().loading.wait_until_check}</span>}
          {isResearch && <span>{strings().loading.wait_until_sign_check}</span>}
        </div>
      )}
      {!isWaiting && order && !order.isGift && (
        <ContainerMd>
          <div className="md:w-9/12">
            <div className="mb-6 md:mb-10">
              {!isResearch && (
                <AlertPrimary
                  type="success"
                  title={strings().checkout.success_title}
                  text={strings().checkout.success_text}
                />
              )}
              {isResearch && (
                <AlertPrimary
                  type="success"
                  title={strings().checkout.sign_success_title}
                  text={strings().checkout.sign_success_text}
                />
              )}

              {!hasExtraSurvey && (
                <div className="mt-10">
                  <NavLink
                    key="profile"
                    to={getLocalizedPath('/', locale, region)}
                    className="!no-underline bg-content-300 text-content-900 hover:bg-content-400 inline-flex px-7 sm:px-9 text-base rounded-2xl py-3.5 font-bold"
                  >
                    {strings().checkout.previewProfile}
                  </NavLink>
                </div>
              )}
              {hasExtraSurvey && (
                <div className="mt-10">
                  <NavLink
                    to={getLocalizedPath(
                      `/questionnaire/repeat/${order.hash}/${order.productId}/${product?.extraSurveyId}`,
                      locale,
                      region,
                    )}
                    className="!no-underline bg-content-300 text-content-900 hover:bg-content-400 inline-flex px-7 sm:px-9 text-base rounded-2xl py-3.5 font-bold"
                  >
                    {strings().checkout.fillQuestionnaire}
                  </NavLink>
                </div>
              )}
            </div>
          </div>
        </ContainerMd>
      )}
    </div>
  );
}
